// "Add person": creates the login, emails them their email + temporary
// password + a one-click sign-in link (Amazon SES), and — if the email could
// not be sent — shows the details so the admin can pass them on.
import React, { useEffect, useState } from 'react';
import { Copy, Check, MailCheck, ShieldCheck } from 'lucide-react';
import { teamApi, passwordProblem } from '../../services/account';
import { Modal, Button, Field, Notice, Segmented, inputClass } from './ui';

const CopyRow = ({ label, value, secret = false }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked; the value is on screen */ }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="w-36 shrink-0 text-xs text-gray-500 dark:text-slate-400">{label}</span>
      <code className={`flex-1 min-w-0 truncate rounded bg-gray-100 dark:bg-slate-800 px-2 py-1 text-xs text-gray-800 dark:text-slate-200 ${secret ? 'font-mono' : ''}`}>{value}</code>
      <button type="button" onClick={copy} className="p-1 text-gray-400 hover:text-blue-600" aria-label={`Copy ${label}`}>
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const CredentialsBox = ({ credentials }) => {
  if (!credentials) return null;
  const all = `Email: ${credentials.email}\nTemporary password: ${credentials.password}\nLog in: ${credentials.link}`;
  return (
    <div className="space-y-2 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-3">
      <CopyRow label="Email" value={credentials.email} />
      <CopyRow label="Temporary password" value={credentials.password} secret />
      <CopyRow label="One-click login link" value={credentials.link} />
      <CopyRow label="All of it" value={all} />
      <p className="text-xs text-amber-800 dark:text-amber-300">This is shown once. The link works one time and expires in 7 days.</p>
    </div>
  );
};

const InviteMemberModal = ({ open, onClose, options, myRole, onInvited }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', role: 'member', start: 'preset:outreach', passwordMode: 'auto', password: '', forceChange: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ firstName: '', lastName: '', email: '', role: 'member', start: 'preset:outreach', passwordMode: 'auto', password: '', forceChange: true });
      setError('');
      setResult(null);
    }
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const submit = async () => {
    setError('');
    if (!form.firstName.trim()) return setError('First name is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Enter a valid email address');
    if (form.passwordMode === 'manual') {
      const problem = passwordProblem(form.password);
      if (problem) return setError(`Password: ${problem.toLowerCase()}`);
    }
    const [kind, key] = form.start.split(':');
    const body = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      role: form.role,
      passwordMode: form.passwordMode,
      password: form.passwordMode === 'manual' ? form.password : undefined,
      forceChange: form.forceChange,
      ...(kind === 'template' ? { templateId: Number(key) } : { presetKey: key }),
    };
    setSaving(true);
    try {
      const res = await teamApi.invite(body);
      setResult({ ...res.data, message: res.message, email: body.email, name: `${body.firstName} ${body.lastName}`.trim() });
      onInvited();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <Modal
        open={open}
        title={result.emailStatus === 'sent' ? 'Invite sent' : 'Person added'}
        onClose={() => onClose()}
        footer={(
          <>
            <Button variant="secondary" onClick={() => onClose()}>Done</Button>
            {form.role === 'member' && (
              <Button onClick={() => onClose({ openAuthorityFor: result.id, name: result.name, email: result.email })}>
                <ShieldCheck className="w-4 h-4" /> Fine-tune their authority
              </Button>
            )}
          </>
        )}
      >
        <div className="space-y-3">
          {result.emailStatus === 'sent' ? (
            <Notice tone="green">
              <span className="inline-flex items-center gap-1.5"><MailCheck className="w-4 h-4" /> {result.message}.</span> They'll get their email,
              a temporary password and a button that signs them straight in.
            </Notice>
          ) : (
            <>
              <Notice tone="amber">{result.message}</Notice>
              <CredentialsBox credentials={result.credentials} />
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Add a team member"
      onClose={() => onClose()}
      footer={(
        <>
          <Button variant="secondary" onClick={() => onClose()}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add and send invite'}</Button>
        </>
      )}
    >
      <div className="space-y-4">
        {error && <Notice>{error}</Notice>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><input className={inputClass} value={form.firstName} onChange={set('firstName')} maxLength={100} autoFocus /></Field>
          <Field label="Last name"><input className={inputClass} value={form.lastName} onChange={set('lastName')} maxLength={100} /></Field>
        </div>
        <Field label="Email" hint="Must not already have its own MARS account.">
          <input className={inputClass} type="email" value={form.email} onChange={set('email')} />
        </Field>

        <Field label="Role">
          <Segmented
            size="md"
            value={form.role}
            onChange={set('role')}
            options={[
              { value: 'member', label: 'Member' },
              ...(myRole === 'owner' ? [{ value: 'co_admin', label: 'Co-admin' }] : []),
            ]}
          />
          <span className="block mt-1 text-xs text-gray-500 dark:text-slate-400">
            {form.role === 'co_admin'
              ? 'Full access to everything and can manage the team. Cannot change billing.'
              : 'Only sees and does what you allow in their authority settings.'}
          </span>
        </Field>

        {form.role === 'member' && (
          <Field label="Start with these permissions" hint="You can change anything afterwards from the shield icon.">
            <select className={inputClass} value={form.start} onChange={set('start')}>
              <optgroup label="Ready-made">
                {(options?.presets || []).map((p) => <option key={p.key} value={`preset:${p.key}`}>{p.name} — {p.description}</option>)}
              </optgroup>
              {(options?.templates || []).length > 0 && (
                <optgroup label="Your templates">
                  {options.templates.map((t) => <option key={t.id} value={`template:${t.id}`}>{t.name}{t.description ? ` — ${t.description}` : ''}</option>)}
                </optgroup>
              )}
            </select>
          </Field>
        )}

        <Field label="Password">
          <Segmented
            size="md"
            value={form.passwordMode}
            onChange={set('passwordMode')}
            options={[{ value: 'auto', label: 'Generate one' }, { value: 'manual', label: 'Set it myself' }]}
          />
        </Field>
        {form.passwordMode === 'manual' && (
          <input className={inputClass} type="text" value={form.password} onChange={set('password')} placeholder="At least 8 characters, with upper, lower case and a number" autoComplete="new-password" />
        )}
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
          <input type="checkbox" checked={form.forceChange} onChange={set('forceChange')} />
          Ask them to choose their own password when they first sign in
        </label>
        <p className="text-xs text-gray-500 dark:text-slate-400">
          We'll email them their login details and a button that signs them in. Uses one of your team seats
          {options?.seats?.limit != null ? ` (${options.seats.used} of ${options.seats.limit} used)` : ''}.
        </p>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
